/**
 * client/src/components/notifications/notification-drawer.tsx
 *
 * Radix Popover panel listing the newest notifications from the aggregated
 * feed (`GET /api/notifications`) — not just OSINT anomalies, which is all the
 * previous version could ever show.
 */

import { motion, useReducedMotion } from "framer-motion";
import * as Popover from "@radix-ui/react-popover";
import { Link } from "wouter";
import { BellOff, AlertTriangle } from "lucide-react";
import { NotificationRow } from "./notification-row";
import {
  NOTIFICATION_DRAWER_LIMIT,
  type NotificationItem,
} from "@shared/notification-types";

/**
 * The feed is already severity-then-recency ordered server-side
 * (`compareNotifications`), so the drawer takes the head of the list — a
 * critical item can never be pushed off by a newer routine one.
 */
export function topItems(items: readonly NotificationItem[]): NotificationItem[] {
  return items.slice(0, NOTIFICATION_DRAWER_LIMIT);
}

interface NotificationDrawerProps {
  readonly rows: NotificationItem[];
  readonly isLoading: boolean;
  readonly forensicPauseActive: boolean;
  readonly forensicPauseBannerId?: string;
  /** True when a source failed — surfaced so an empty list is never read as "all clear". */
  readonly degraded: boolean;
  readonly onClose: () => void;
}

export function NotificationDrawer({
  rows,
  isLoading,
  forensicPauseActive,
  degraded,
  onClose,
}: NotificationDrawerProps): JSX.Element {
  const prefersReducedMotion = useReducedMotion();

  return (
    <Popover.Portal>
      <Popover.Content
        sideOffset={8}
        align="end"
        className="z-50 w-80 rounded-[14px] border border-border-default bg-bg-primary shadow-lg outline-none"
        data-testid="notification-drawer"
      >
        <div className="flex items-center justify-between px-3 py-2.5 border-b border-border-subtle">
          <span className="text-xs font-semibold text-text-primary">Notifications</span>
          <Link
            href="/alerts"
            onClick={onClose}
            className="text-[10px] font-semibold text-interactive hover:underline"
            data-testid="view-all-link"
          >
            View all →
          </Link>
        </div>

        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: prefersReducedMotion ? 0 : 0.22 }}
          data-testid="drawer-body"
        >
          {isLoading && (
            <div className="space-y-1 p-2" data-testid="drawer-skeleton">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="h-14 rounded-[10px] bg-bg-elevated border border-border-default animate-pulse"
                />
              ))}
            </div>
          )}

          {/*
            Honest partial-failure notice: if a source failed, an empty or short
            list must NOT read as "nothing needs your attention" (Friction Rule
            clause 2 — no false assurance). Amber, never red: this is a load
            problem, not a Forensic Pause.
          */}
          {!isLoading && degraded && (
            <div
              className="flex items-start gap-2 px-3 py-2 border-b border-border-subtle"
              data-testid="drawer-degraded"
            >
              <AlertTriangle size={12} className="text-warning mt-0.5 shrink-0" aria-hidden />
              <p className="text-[10px] text-text-secondary">
                Some notifications could not be loaded. This list may be incomplete.
              </p>
            </div>
          )}

          {!isLoading && rows.length === 0 && (
            <div
              className="flex flex-col items-center justify-center py-8 gap-2 text-center"
              data-testid="drawer-empty"
            >
              <BellOff size={20} className="text-text-tertiary" aria-hidden />
              {/*
                Scoped copy, deliberately NOT "Nothing needs your attention."
                The feed is knowingly partial (no payment-confirmation, workflow-
                approval, guardian-duty or inbound-connection sources yet), so a
                claim about the user's whole obligation surface would be false
                assurance (CLAUDE.md §Friction Rule clause 2).
              */}
              <p className="text-xs text-text-secondary">No open items in this view.</p>
            </div>
          )}

          {!isLoading && rows.length > 0 && (
            <div className="divide-y divide-border-subtle">
              {rows.map((item) => (
                <NotificationRow
                  key={item.id}
                  item={item}
                  forensicPauseActive={forensicPauseActive}
                  onNavigate={onClose}
                />
              ))}
            </div>
          )}
        </motion.div>
      </Popover.Content>
    </Popover.Portal>
  );
}
